/**
 * Server state for My Sessions (HS-1440).
 *
 * **The only file in this feature that names the transport**, which since HS-1481 is the generated
 * Admin API client rather than the mock. Nothing else in the feature moved with it — not the
 * adapter, not a component, not a test of either — which is what that separation was for.
 *
 * The mappers below exist because the generated DTOs and ours disagree about absence rather than
 * about shape: the API omits an optional key where our types say `null`, and every string in the
 * document is marked nullable even where the contract guarantees one.
 *
 * `queryOptions()` is hand-built rather than taken from Orval's generated hook, matching the other
 * portal data layers (`features/dashboard/dashboard.api.ts`): the generated helper adds an
 * `enabled` guard that `useSuspenseQuery` rejects.
 */
import {
  getApiPortalMeSessions,
  getApiPortalMeSessionsId,
  getApiPortalMeSessionsIdPlayback,
  postApiPortalMeSessionsIdIssue,
  type GetApiPortalMeSessionsParams,
  type PortalPlaybackDto,
  type PortalSessionDetailDto,
  type PortalSessionListDto,
  type PortalSessionListItemDto,
} from '@smartsash/api-client';
import { queryOptions } from '@tanstack/react-query';

import { toSessionDetail, toSessionPlayback, toSessionsView } from './sessions.adapter';
import type {
  ReportIssueDto,
  ReportIssueParams,
  SessionDetail,
  SessionDetailDto,
  SessionListDto,
  SessionListItemDto,
  SessionListParams,
  SessionPlayback,
  SessionPlaybackDto,
  SessionScoreDto,
  SessionsView,
} from './sessions.types';

/**
 * The session is not this user's — contract §4's 404.
 *
 * Declared here, in permanent code, rather than in a route: a route that imported the mock's own
 * error would have stopped recognising a 404 the day the real endpoint answered one. That day has
 * come, and only the `catch` below changed.
 */
export class SessionNotFoundError extends Error {
  constructor(readonly sessionId: string) {
    super(`No session "${sessionId}"`);
    this.name = 'SessionNotFoundError';
  }
}

/** HTTP status the contract uses for "no such session, or not yours". Never a 403. */
const STATUS_NOT_FOUND = 404;

/** Translate a 404 from the client into the one error the route knows about. */
async function readSession<T>(sessionId: string, read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (error) {
    if ((error as { status?: number } | null)?.status === STATUS_NOT_FOUND) {
      throw new SessionNotFoundError(sessionId);
    }
    throw error;
  }
}

/**
 * The query string the API accepts is entirely `string`-typed, because the document declares it
 * that way, while ours is typed by what the filters mean. Converting here keeps the numbers
 * numbers everywhere the UI reasons about them, and turns them into text exactly once.
 *
 * Repeated keys (`labId`, `deviceId`) pass through as arrays: axios serialises them as repeated
 * parameters, which is what the contract ORs together.
 */
function toListParams(params: SessionListParams): GetApiPortalMeSessionsParams {
  const text = (value: number | undefined) => (value === undefined ? undefined : String(value));

  return {
    page: text(params.page),
    pageSize: text(params.pageSize),
    sort: params.sort,
    q: params.q,
    from: params.from,
    to: params.to,
    durationMinSeconds: text(params.durationMinSeconds),
    durationMaxSeconds: text(params.durationMaxSeconds),
    timeFrom: params.timeFrom,
    timeTo: params.timeTo,
    labId: params.labId,
    deviceId: params.deviceId,
    safety: params.safety,
    recording: params.recording,
    scoreMin: text(params.scoreMin),
    scoreMax: text(params.scoreMax),
  };
}

/**
 * Ours is a discriminated union and the generated shape is flat, which is the one place the two
 * genuinely differ rather than merely disagreeing about absence. The contract carries `value`
 * only for `scored`, so narrowing here is what lets every consumer read `score.value` without a
 * guard — and a `scored` row arriving without one is a contract violation worth saying out loud.
 */
function toScoreDto(score: PortalSessionListItemDto['score']): SessionScoreDto {
  if (score.state !== 'scored') return { state: score.state };
  if (score.value === undefined || score.value === null) {
    throw new Error('Admin API sent a scored session with no score value');
  }
  return { state: 'scored', value: score.value };
}

/**
 * The row every list and the detail share. Extracted rather than repeated because the detail DTO
 * *is* a row plus five fields, and two copies of this would drift the first time one changed.
 */
function toRowDto(item: PortalSessionListItemDto | PortalSessionDetailDto): SessionListItemDto {
  return {
    id: item.id ?? '',
    startedAt: item.startedAt,
    endedAt: item.endedAt ?? null,
    durationSeconds: item.durationSeconds,
    awaySeconds: item.awaySeconds,
    timeZone: item.timeZone ?? '',
    lab: item.lab == null ? null : { id: item.lab.id, name: item.lab.name ?? '' },
    device: { id: item.device.id, code: item.device.code ?? '' },
    safety: { status: item.safety.status, reason: item.safety.reason ?? null },
    recording: item.recording,
    score: toScoreDto(item.score),
    status: item.status,
  };
}

function toListDto(dto: PortalSessionListDto): SessionListDto {
  return {
    page: dto.page,
    pageSize: dto.pageSize,
    totalCount: dto.totalCount,
    totalUnfiltered: dto.totalUnfiltered,
    scoring: dto.scoring,
    items: (dto.items ?? []).map(toRowDto),
  };
}

/**
 * `overrides` is the one field where absence carries meaning rather than being noise: the design
 * words "None logged this session" from a null and would render `{ count: 0 }` differently, so
 * the two are kept apart here instead of being defaulted together.
 */
function toDetailDto(dto: PortalSessionDetailDto): SessionDetailDto {
  return {
    ...toRowDto(dto),
    formulaVersion: dto.formulaVersion ?? '',
    sashClosedOnExit: dto.sashClosedOnExit,
    overrides: dto.overrides ?? null,
    awaySegments: dto.awaySegments ?? [],
    factors: (dto.factors ?? []).map((factor) => ({
      key: factor.key,
      weightPercent: factor.weightPercent,
      score: factor.score,
      points: factor.points,
      maxPoints: factor.maxPoints,
      severity: factor.severity,
      window:
        factor.window.kind === 'rolling'
          ? {
              kind: 'rolling' as const,
              days: factor.window.days ?? 0,
              qualifyingSessions: factor.window.qualifyingSessions ?? 0,
            }
          : { kind: 'session' as const },
      observations: (factor.observations ?? []).map((observation) => ({
        kind: observation.kind,
        key: observation.key ?? '',
        params: (observation.params ?? {}) as Record<string, number | string>,
      })),
    })),
  };
}

/**
 * Three shapes behind one `kind`, and the fields that make each one usable are optional in the
 * schema. A `recording` without its URL, or a `none` without its reason, is a payload the player
 * cannot act on, so it fails here rather than rendering an empty video surface.
 */
function toPlaybackDto(dto: PortalPlaybackDto): SessionPlaybackDto {
  if (dto.kind === 'none') {
    if (dto.reason == null) {
      throw new Error('Admin API sent playback kind "none" with no reason');
    }
    return { kind: 'none', reason: dto.reason };
  }

  const credential = {
    wsUrl: text(dto.wsUrl, 'playback.wsUrl'),
    token: text(dto.token, 'playback.token'),
    expiresAt: text(dto.expiresAt, 'playback.expiresAt'),
  };

  if (dto.kind === 'live') return { kind: 'live', ...credential };

  return {
    kind: 'recording',
    ...credential,
    recordingUrl: text(dto.recordingUrl, 'playback.recordingUrl'),
    durationSeconds: dto.durationSeconds ?? 0,
  };
}

/**
 * Every string in the document is marked nullable, so a value the contract guarantees still
 * arrives as `string | null`. A null there is a contract violation, and naming the field beats a
 * player pointed at the empty string.
 */
function text(value: string | null | undefined, field: string): string {
  if (value === null || value === undefined) {
    throw new Error(`Admin API sent no "${field}", which the contract guarantees`);
  }
  return value;
}

/**
 * How long a playback credential is trusted.
 *
 * Shorter than the token's own lifetime on purpose: a refetch that arrives just before expiry is
 * cheap, and a player that mounts with a dead token shows the "no video" surface for a session
 * that does have one.
 */
const PLAYBACK_STALE_MINUTES = 5;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;
const PLAYBACK_STALE_MS = PLAYBACK_STALE_MINUTES * SECONDS_PER_MINUTE * MS_PER_SECOND;

/**
 * Cache keys.
 *
 * `root` is what invalidation targets — `useSashIqRealtime` refreshes it on a `SashIQEvent`,
 * because a session moving from `pending` to a score *is* that event, and the dashboard's
 * end-session write invalidates it too.
 */
export const sessionsKeys = {
  root: ['portal', 'sessions'] as const,
  list: (params: SessionListParams) => ['portal', 'sessions', 'list', params] as const,
  detail: (sessionId: string) => ['portal', 'sessions', 'detail', sessionId] as const,
  playback: (sessionId: string) => ['portal', 'sessions', 'playback', sessionId] as const,
};

export function sessionsQuery(params: SessionListParams) {
  return queryOptions<SessionsView>({
    queryKey: sessionsKeys.list(params),
    queryFn: async ({ signal }) =>
      toSessionsView(
        toListDto(await getApiPortalMeSessions(toListParams(params), undefined, signal)),
      ),
  });
}

export function sessionDetailQuery(sessionId: string) {
  return queryOptions<SessionDetail>({
    queryKey: sessionsKeys.detail(sessionId),
    queryFn: async () =>
      readSession(sessionId, async () =>
        toSessionDetail(toDetailDto(await getApiPortalMeSessionsId(sessionId))),
      ),
  });
}

export function sessionPlaybackQuery(sessionId: string) {
  return queryOptions<SessionPlayback>({
    queryKey: sessionsKeys.playback(sessionId),
    queryFn: async () =>
      readSession(sessionId, async () =>
        toSessionPlayback(toPlaybackDto(await getApiPortalMeSessionsIdPlayback(sessionId))),
      ),
    staleTime: PLAYBACK_STALE_MS,
    // A credential is worth nothing after a reload of the tab, and refetching it on focus is how
    // a player left open over lunch reconnects rather than failing silently.
    gcTime: PLAYBACK_STALE_MS,
  });
}

/** `POST /api/portal/me/sessions/{id}/issue`. Returns the created report, per contract §7. */
export async function reportSessionIssue(params: ReportIssueParams): Promise<ReportIssueDto> {
  const created = await postApiPortalMeSessionsIdIssue(params.sessionId, {
    message: params.message,
  });
  return { id: text(created.id, 'issue.id'), createdAt: created.createdAt };
}
